import { useEffect, useMemo, useState } from 'react'
import {
    getActiveAbTestConfig,
    getPatternByPercentage,
    isTargetVisitor,
} from '../core'
import { getAbValueFromCookie, setAbValueToCookie } from '../utils/cookie'
import type { AbPattern } from '../config/types'
import { DEFAULT_PATTERN } from '../config/types'

interface UseAbTestPatternOptions {
    abValue?: number
    currentPath?: string
    currentSearch?: string
}

interface UseAbTestPatternReturn {
    pattern: AbPattern
    isA: boolean
    isB: boolean
    isC: boolean
    isD: boolean
    /**
     * パターンの判定が確定したか（cookie読み取り後のtrue）。
     * 初回レンダーではcookieを読めず必ず DEFAULT_PATTERN('A') を返すため、
     * 「Aのときだけ描画する要素」はそのままだとB/C割当でも一瞬DOMに載る。
     * data-view-labelを持つ要素ではGTMが誤発火し、A側の計測値を汚染しうる。
     * それを避けたい箇所では `isResolved && isA` のように判定確定を待つ（DEV-3379）。
     *
     * なお Aパターンを持たないconfig（DEV-3390のB/C/D）では、`isA` は
     * 「未解決 または テスト対象外」の意味しか持たない。描画の出し分けに `isA` を使わず、
     * `isC`/`isD` 側で分岐して既定枝（＝対照群の見た目）を描くこと。
     */
    isResolved: boolean
}

export const useAbTestPattern = (
    options: UseAbTestPatternOptions = {}
): UseAbTestPatternReturn => {
    const {
        abValue,
        currentPath = typeof window !== 'undefined' ? window.location.pathname : '/',
        currentSearch = typeof window !== 'undefined' ? window.location.search : '',
    } = options

    const [isClient, setIsClient] = useState(false)
    // null = 未割当（テスト対象外、または判定前）
    const [clientAbValue, setClientAbValue] = useState<number | null>(null)

    useEffect(() => {
        setIsClient(true)
        if (abValue !== undefined) {
            setClientAbValue(abValue)
            return
        }

        const activeConfig = getActiveAbTestConfig(currentPath)
        if (!activeConfig) return

        const cookieValue = getAbValueFromCookie(activeConfig.issueNumber)
        if (cookieValue !== null) {
            // 既に割当済み＝初回に対象条件を満たして参加した人。以降は条件を見ずに同じ割当を保つ
            setClientAbValue(cookieValue)
            return
        }

        // 新規。対象条件を満たさない流入にはcookieを書かない。
        // 「対象外」を焼き付けないことで、後から条件を満たすURLで来たときに参加できる
        if (!isTargetVisitor(activeConfig, currentSearch)) return

        const newAbValue = Math.floor(Math.random() * 10)
        setAbValueToCookie(activeConfig.issueNumber, newAbValue)
        setClientAbValue(newAbValue)
    }, [abValue, currentPath, currentSearch])

    const pattern: AbPattern = useMemo(() => {
        if (!isClient || clientAbValue === null) return DEFAULT_PATTERN
        const active = getActiveAbTestConfig(currentPath)
        if (!active) return DEFAULT_PATTERN
        return getPatternByPercentage(clientAbValue, active.patterns)
    }, [isClient, clientAbValue, currentPath])

    return {
        pattern,
        isA: pattern === DEFAULT_PATTERN,
        isB: pattern === 'B',
        isC: pattern === 'C',
        isD: pattern === 'D',
        isResolved: isClient,
    }
}


